/**
 * INVOICE GENERATOR
 * Modal component for generating invoices
 */

import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import supabase from '../../../lib/supabaseClient';
import fetchWithCsrf from '../../../lib/fetchWithCsrf';
import apiClient from '../../../lib/apiClient';

export default function InvoiceGenerator({ isOpen, onClose, onGenerated, prefilledTenantId = null }) {
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [preview, setPreview] = useState(null);
  const [formData, setFormData] = useState({
    tenant_id: prefilledTenantId || '',
    period_start: '',
    period_end: '',
    notes: ''
  });

  // Load customers on mount
  useEffect(() => {
    if (isOpen) {
      loadCustomers();
      // Set default date range (last month)
      const today = new Date();
      const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      setFormData(prev => ({
        ...prev,
        period_start: lastMonthStart.toISOString().split('T')[0],
        period_end: lastMonthEnd.toISOString().split('T')[0]
      }));
    }
  }, [isOpen]);

  const loadCustomers = async () => {
    try {
      const { data, error } = await supabase
        .from('users_profile')
        .select('tenant_id, email, full_name')
        .order('full_name');

      if (error) throw error;

      // Group by tenant_id
      const uniqueTenants = {};
      data.forEach(profile => {
        if (!uniqueTenants[profile.tenant_id]) {
          uniqueTenants[profile.tenant_id] = {
            tenant_id: profile.tenant_id,
            name: profile.full_name || profile.email,
            email: profile.email
          };
        }
      });

      setCustomers(Object.values(uniqueTenants));
    } catch (err) {
      console.error('Error loading customers:', err);
    }
  };

  const handlePreview = async () => {
    if (!formData.tenant_id || !formData.period_start || !formData.period_end) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.get('/admin/financials/cost-events?' + new URLSearchParams({
        tenant_id: formData.tenant_id,
        date_from: formData.period_start,
        date_to: formData.period_end,
        limit: 5  // Only fetch first 5 for preview display; use summary for totals
      }));

      const result = response.data;

      if (!result.pagination || result.pagination.total === 0) {
        alert('No cost events found for this period');
        setPreview(null);
        return;
      }

      // Use server-computed summary total (covers ALL events, not just this page)
      const totalCost = parseFloat(result.summary?.total_cost || 0);
      const totalEvents = result.pagination.total;

      setPreview({
        total_cost: totalCost,
        total_events: totalEvents,
        events: result.data.slice(0, 5)
      });
    } catch (err) {
      console.error('Error previewing invoice:', err);
      alert('Error loading invoice preview');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!formData.tenant_id || !formData.period_start || !formData.period_end) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await fetchWithCsrf('/api/admin/financials/invoices/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.error) throw new Error(result.error);

      alert(`Invoice ${result.invoice.invoice_number} generated successfully!`);
      if (onGenerated) onGenerated(result.invoice);
      onClose();
    } catch (err) {
      console.error('Error generating invoice:', err);
      alert('Error generating invoice: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Generate Invoice</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Body */}
          <div className="bg-white px-6 py-4 space-y-4">
            {/* Customer Select */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Customer *</label>
              <select
                value={formData.tenant_id}
                onChange={(e) => setFormData(prev => ({ ...prev, tenant_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                disabled={prefilledTenantId}
              >
                <option value="">Select a customer...</option>
                {customers.map(customer => (
                  <option key={customer.tenant_id} value={customer.tenant_id}>
                    {customer.name} ({customer.email})
                  </option>
                ))}
              </select>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Period Start *</label>
                <input
                  type="date"
                  value={formData.period_start}
                  onChange={(e) => setFormData(prev => ({ ...prev, period_start: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Period End *</label>
                <input
                  type="date"
                  value={formData.period_end}
                  onChange={(e) => setFormData(prev => ({ ...prev, period_end: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Optional notes for this invoice..."
              />
            </div>

            {/* Preview Button */}
            <button
              onClick={handlePreview}
              disabled={loading}
              className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Preview Line Items'}
            </button>

            {/* Preview */}
            {preview && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <h4 className="font-medium text-gray-900">Invoice Preview</h4>
                <p className="text-sm text-gray-600">
                  <strong>{preview.total_events}</strong> cost events totaling <strong>${preview.total_cost.toFixed(2)}</strong>
                </p>
                <div className="text-xs text-gray-500 space-y-1">
                  <p>Sample events:</p>
                  {preview.events.map((event, idx) => (
                    <p key={idx}>• {event.event_type} - ${parseFloat(event.total_cost).toFixed(4)}</p>
                  ))}
                  {preview.total_events > 5 && <p>... and {preview.total_events - 5} more</p>}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={loading || !preview}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Generating...' : 'Generate Invoice'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
